import { domEventTypes, instrumentTypes } from '../../constants';
import { createEventDescription } from '../../utils/description';
import { triggerHandlers } from '../../utils/handlers';
import { htmlTreeAsString } from '../../utils/htmlTreeAsString';
/**
 *	This code was gladly borrowed from
 *	https://github.com/getsentry/sentry-javascript/blob/d136f4e0bca43cda21db42da4e84bbe3448a70a1/packages/utils/src/instrument.ts
 *  Please note that there might be a leak tweak or two :)
 */ import { fill } from '../../utils/object';
const debounceDuration = 1000;
let debounceTimerID;
let lastCapturedEvent;
const createDOMInteractionEvent = createEventDescription(instrumentTypes.DOM);
/**
 * Decide whether the current event should finish the debounce of previously captured one.
 * @param previous previously captured event
 * @param current event to be captured
 */ const shouldShortcircuitPreviousDebounce = (previous, current)=>{
    // If there was no previous event, it should always be swapped for the new one.
    if (!previous) {
        return true;
    }
    // If both events have different type, then user definitely performed two separate actions. e.g. click + keypress.
    if (previous.type !== current.type) {
        return true;
    }
    try {
        // If both events have the same type, it's still possible that actions were performed on different targets.
        // e.g. 2 clicks on different buttons.
        if (previous.target !== current.target) {
            return true;
        }
    } catch (_e) {
    // just accessing `target` property can throw an exception in some rare circumstances
    // see: https://github.com/getsentry/sentry-javascript/issues/838
    }
    // If both events have the same type _and_ same `target` (an element which triggered an event, _not necessarily_
    // to which an event listener was attached), we treat them as the same action, as we want to capture
    // only one breadcrumb. e.g. multiple clicks on the same button, or typing inside a user input box.
    return false;
};
/**
 * Decide whether an event should be captured.
 * @param event event to be captured
 */ const shouldSkipDOMEvent = (event)=>{
    // We are only interested in filtering `keypress` events for now.
    if (event.type !== domEventTypes.keypress) {
        return false;
    }
    try {
        const target = event.target;
        if (!target || !target.tagName) {
            return true;
        }
        // Only consider keypress events on actual input elements. This will disregard keypresses targeting body
        // e.g.tabbing through elements, hotkeys, etc.
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return false;
        }
    } catch (_e) {
    // just accessing `target` property can throw an exception in some rare circumstances
    // see: https://github.com/getsentry/sentry-javascript/issues/838
    }
    return true;
};
/**
 * Wraps addEventListener to capture UI breadcrumbs
 * @param handler function that will be triggered
 * @param globalListener indicates whether event was captured by the global event listener
 * @returns wrapped breadcrumb events handler
 * @hidden
 */ const makeDOMEventHandler = (handler, globalListener = false)=>(event)=>{
        // It's possible this handler might trigger multiple times for the same
        // event (e.g. event propagation through node ancestors).
        // Ignore if we've already captured that event.
        if (!event || lastCapturedEvent === event) {
            return;
        }
        // We always want to skip _some_ events.
        if (shouldSkipDOMEvent(event)) {
            return;
        }
        const target = event.target;
        // Check if we have a valid element
        if (!target || typeof target?.getAttribute !== 'function') {
            return;
        }
        const description = {
            value: target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target.value : undefined,
            id: target.getAttribute('id'),
            type: target.getAttribute('type'),
            name: target.getAttribute('name'),
            classes: target.getAttribute('class'),
            globalListener,
            eventType: event.type,
            htmlTree: '<unknown>'
        };
        try {
            // keyAttrs zatim nepouzivame
            const keyAttrs = undefined;
            // const keyAttrs = typeof dom === 'object' ? dom.serializeAttribute : undefined;
            // if (typeof keyAttrs === 'string') {
            // 	keyAttrs = [keyAttrs];
            // }
            description.htmlTree = target ? htmlTreeAsString(target, keyAttrs) : htmlTreeAsString(event, keyAttrs);
        } catch (_e) {}
        /* If there is no debounce timer,
		 * it means that we can safely capture the new event and store it for future comparisons.
		 */ if (debounceTimerID === undefined) {
            handler(createDOMInteractionEvent(description));
            lastCapturedEvent = event;
        // If there is a debounce awaiting, see if the new event is different enough to treat it as a unique one.
        // If that's the case, emit the previous event and store locally the newly-captured DOM event.
        } else if (shouldShortcircuitPreviousDebounce(lastCapturedEvent, event)) {
            handler(createDOMInteractionEvent(description));
            lastCapturedEvent = event;
        }
        /* Start a new debounce timer.
		 * That will prevent us from capturing multiple events that should be grouped together.
		 */ clearTimeout(debounceTimerID);
        debounceTimerID = setTimeout(()=>{
            debounceTimerID = undefined;
        }, debounceDuration);
    };
/** JSDoc */ export const instrumentDOM = ()=>{
    if (!('document' in window)) {
        return;
    }
    // Make it so that any click or keypress that is unhandled / bubbled up all the way to the document triggers our dom
    // handlers. (Normally we have only one, which captures a breadcrumb for each click or keypress.) Do this before
    // we instrument `addEventListener` so that we don't end up attaching this handler twice.
    const triggerDOMHandler = triggerHandlers.bind(null, instrumentTypes.DOM);
    const globalDOMEventHandler = makeDOMEventHandler(triggerDOMHandler, true);
    document.addEventListener(domEventTypes.click, globalDOMEventHandler, false);
    document.addEventListener(domEventTypes.keypress, globalDOMEventHandler, false);
    // After hooking into click and keypress events bubbled up to `document`, we also hook into user-handled
    // clicks & keypresses, by adding an event listener of our own to any element to which they add a listener. That
    // way, whenever one of their handlers is triggered, ours will be, too. (This is needed because their handler
    // could potentially prevent the event from bubbling up to our global listeners. This way, our handler are still
    // guaranteed to fire at least once.)
    [
        'EventTarget',
        'Node'
    ].forEach((target)=>{
        const proto = window[target]?.prototype;
        if (!proto || !proto.hasOwnProperty || !proto.hasOwnProperty('addEventListener')) {
            return;
        }
        fill(proto, 'addEventListener', function(originalAddEventListener) {
            return function(type, listener, options) {
                if (type === domEventTypes.click || type === domEventTypes.keypress) {
                    try {
                        this.__apm_instrumentation_handlers__ = this.__apm_instrumentation_handlers__ || {};
                        const handlers = this.__apm_instrumentation_handlers__;
                        handlers[type] = handlers[type] || {
                            refCount: 0
                        };
                        const handlerForType = handlers[type];
                        if (!handlerForType.handler) {
                            const handler = makeDOMEventHandler(triggerDOMHandler);
                            handlerForType.handler = handler;
                            originalAddEventListener.call(this, type, handler, options);
                        }
                        handlerForType.refCount += 1;
                    } catch (_e) {
                    // Accessing dom properties is always fragile.
                    // Also allows us to skip `addEventListenrs` calls with no proper `this` context.
                    }
                }
                return originalAddEventListener.call(this, type, listener, options);
            };
        });
        fill(proto, 'removeEventListener', function(originalRemoveEventListener) {
            return function(type, listener, options) {
                if (type === domEventTypes.click || type === domEventTypes.keypress) {
                    try {
                        const handlers = this.__apm_instrumentation_handlers__ || {};
                        const handlerForType = handlers[type];
                        if (handlerForType) {
                            handlerForType.refCount -= 1;
                            /*
							 * If there are no longer any custom handlers of the current type on this element,
							 * we can remove ours, too.
							 */ if (handlerForType.refCount <= 0) {
                                originalRemoveEventListener.call(this, type, handlerForType.handler, options);
                                handlerForType.handler = undefined;
                                delete handlers[type];
                            }
                            /*
							 * If there are no longer any custom handlers of any type on this element,
							 * cleanup everything.
							 */ if (Object.keys(handlers).length === 0) {
                                delete this.__apm_instrumentation_handlers__;
                            }
                        }
                    } catch (_e) {
                    // Accessing dom properties is always fragile.
                    // Also allows us to skip `addEventListenrs` calls with no proper `this` context.
                    }
                }
                return originalRemoveEventListener.call(this, type, listener, options);
            };
        });
    });
};
